import React, { useState, useEffect } from 'react'
import spektrixAuth from '@lib/spektrix/auth'
import EditDetails from './edit-details'
import ChangePassword from './change-password'
import ManageAddresses from './manage-addresses'
import CommunicationPreferences from './communication-preferences'
import PrintHomeDocuments from './print-home-documents'
import LoginModal from '../login-modal'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

const MyAccount = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showLoginModal, setShowLoginModal] = useState(false)
  
  // Get initial tab from URL
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    return ['details', 'password', 'addresses', 'communications', 'documents'].includes(tab) ? tab : 'details'
  }
  
  const [activeTab, setActiveTab] = useState(getInitialTab())

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      try {
        const authStatus = await spektrixAuth.checkAuthStatus()
        setIsAuthenticated(authStatus.authenticated)
        setCurrentUser(authStatus.user)
        
        if (!authStatus.authenticated) {
          setShowLoginModal(true)
        }
      } catch (error) {
        console.error('Failed to check auth status:', error)
        setIsAuthenticated(false)
        setShowLoginModal(true)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()

    // Listen for auth changes
    const cleanup = spektrixAuth.onAuthChange((detail) => {
      setIsAuthenticated(detail.authenticated)
      setCurrentUser(detail.user)
      if (detail.authenticated && showLoginModal) {
        setShowLoginModal(false)
      }
    })

    return cleanup
  }, [])

  const handleLoginClose = () => {
    // If user closes login modal without logging in, redirect to home
    if (!isAuthenticated) {
      window.location.href = '/'
    } else {
      setShowLoginModal(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="alert alert-warning">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Please log in to access your account.</span>
        </div>
        {showLoginModal && (
          <LoginModal 
            isOpen={showLoginModal}
            onClose={handleLoginClose}
            onLoginSuccess={() => setShowLoginModal(false)}
          />
        )}
      </>
    )
  }

  const tabs = [
    { id: 'details', label: 'Personal Details', icon: '👤' },
    { id: 'password', label: 'Change Password', icon: '🔑' },
    { id: 'addresses', label: 'Addresses', icon: '📍' },
    { id: 'communications', label: 'Communications', icon: '📧' },
    { id: 'documents', label: 'Print at Home', icon: '📄' }
  ]
  
  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    // Update URL without page reload
    const url = new URL(window.location)
    url.searchParams.set('tab', tabId)
    window.history.pushState({}, '', url)
  }

  return (
    <div className="space-y-6">
      {/* Welcome message */}
      {currentUser && (
        <div className="bg-gray-800 rounded-lg p-4">
          <p className="text-lg text-white">
            Welcome back, <span className="font-semibold">{currentUser.firstName} {currentUser.lastName}</span>
          </p>
        </div>
      )}

      {/* Tab navigation - Dropdown on mobile, tabs on desktop */}
      <div>
        {/* Mobile dropdown */}
        <div className="sm:hidden">
          <div className="relative">
            <select
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
              className="w-full appearance-none bg-gray-800 text-white px-4 py-3 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {tabs.map(tab => (
                <option key={tab.id} value={tab.id}>
                  {tab.icon} {tab.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Desktop tabs */}
        <div className="hidden sm:flex flex-wrap gap-2 bg-gray-800 p-2 rounded-lg">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
              }`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="bg-gray-800 rounded-lg shadow-sm">
        <div className="p-6">
          {activeTab === 'details' && <EditDetails user={currentUser} />}
          {activeTab === 'password' && <ChangePassword />}
          {activeTab === 'addresses' && <ManageAddresses />}
          {activeTab === 'communications' && <CommunicationPreferences />}
          {activeTab === 'documents' && <PrintHomeDocuments />}
        </div>
      </div>
    </div>
  )
}

export default MyAccount